import { Stack } from "@mui/material";
import { isBrowserLightMode } from "@pulse/shared-components";
import { useFoundationColorTokens } from "@surya-digital/leo-reactjs-material-ui";
import React, { PropsWithChildren, useLayoutEffect, useRef } from "react";
import { ChatSurveyHeader, ChatSurveyHeaderProps } from "./ChatSurveyHeader";
import {
  CHAT_DESKTOP_HEADER_BREAKPOINT,
  CHAT_DESKTOP_HEADER_HEIGHT,
  CHAT_MOBILE_HEADER_HEIGHT,
  CHAT_TIMELINE_VERTICAL_GUTTER,
} from "./ChatSurveyLayout";

const CHAT_LIGHT_BACKGROUND = "#F2F0EF";

export type ChatSurveySectionProps = PropsWithChildren<
  ChatSurveyHeaderProps & {
    isHeaderHidden?: boolean;
    isOuterScrollLocked?: boolean;
  }
>;

export const ChatSurveySection = ({
  children,
  isHeaderHidden = false,
  isOuterScrollLocked = false,
  ...headerProps
}: ChatSurveySectionProps): React.ReactElement => {
  const tokens = useFoundationColorTokens();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const header = headerRef.current;
    const content = contentRef.current;

    if (!scrollContainer || !header || !content || isOuterScrollLocked) {
      return undefined;
    }

    let lastScrollTop = scrollContainer.scrollTop;
    let hiddenOffset = 0;

    const applyOffset = (): void => {
      const transform = hiddenOffset ? `translateY(${-hiddenOffset}px)` : "";

      header.style.transform = transform;
      content.style.transform = transform;
    };

    const resetOffset = (): void => {
      hiddenOffset = 0;
      applyOffset();
    };

    const handleScroll = (): void => {
      const currentScrollTop = scrollContainer.scrollTop;
      const scrollDelta = currentScrollTop - lastScrollTop;
      const headerHeight = header.getBoundingClientRect().height;

      hiddenOffset = Math.max(
        0,
        Math.min(headerHeight, hiddenOffset + scrollDelta),
      );

      if (currentScrollTop === 0) {
        hiddenOffset = 0;
      }

      applyOffset();
      lastScrollTop = currentScrollTop;
    };

    const resizeObserver = new ResizeObserver(() => {
      hiddenOffset = Math.min(
        hiddenOffset,
        header.getBoundingClientRect().height,
      );
      applyOffset();
    });

    resizeObserver.observe(header);
    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return (): void => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", handleScroll);
      resetOffset();
    };
  }, [isHeaderHidden, isOuterScrollLocked]);

  return (
    <Stack
      height="100%"
      width="100%"
      minHeight={0}
      data-chat-scroll-container
      data-chat-outer-scroll-locked={isOuterScrollLocked ? "" : undefined}
      ref={scrollContainerRef}
      sx={{
        containerType: "size",
        "--chat-header-height": isHeaderHidden
          ? "0px"
          : CHAT_MOBILE_HEADER_HEIGHT,
        "--chat-timeline-vertical-gutter": CHAT_TIMELINE_VERTICAL_GUTTER,
        ...(isHeaderHidden
          ? {}
          : {
              [`@media (min-width:${CHAT_DESKTOP_HEADER_BREAKPOINT})`]: {
                "--chat-header-height": CHAT_DESKTOP_HEADER_HEIGHT,
              },
            }),
        overflowY: isOuterScrollLocked ? "hidden" : "auto",
        // Let the timeline choose when to scroll as questions are added.
        overflowAnchor: "none",
        overscrollBehavior: "contain",
        "&[data-chat-outer-scroll-locked] [data-chat-item-body]": {
          overscrollBehaviorY: "contain",
        },
        backgroundColor: isBrowserLightMode()
          ? CHAT_LIGHT_BACKGROUND
          : tokens.background,
      }}
    >
      {!isHeaderHidden && (
        <Stack
          ref={headerRef}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            willChange: "transform",
          }}
        >
          <ChatSurveyHeader {...headerProps} />
        </Stack>
      )}
      <Stack
        component="main"
        ref={contentRef}
        flex="1 0 auto"
        width="100%"
        boxSizing="border-box"
        sx={{
          willChange: "transform",
          paddingInline: "16px",
          "@media (min-width:600px)": {
            paddingInline: "64px",
          },
          "@media (min-width:1200px)": {
            width: "100%",
            maxWidth: "960px",
            marginInline: "auto",
            paddingInline: 0,
          },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};
